// Formatting helpers

/** Title-case a name: "MARIO ROSSI" -> "Mario Rossi" */
export function formatName(s) {
  if (!s) return "";
  return String(s)
    .toLowerCase()
    .split(/(\s+|-)/) // keep separators
    .map(w => /^\s+$|^-$/.test(w) ? w : (w.charAt(0).toUpperCase() + w.slice(1)))
    .join("");
}

/** Smarter Title Case for full names: preserves common particles (di, da, de, van, von…) */
const LC_PARTICLES = new Set(["di","da","de","del","della","dello","dei","delle","van","von","der","den","dos","das","y"]);
export function titleCaseName(s) {
  if (!s) return "";
  const cleaned = String(s).trim().replace(/\s+/g, " ");
  return cleaned.split(" ").map((w, i) => {
    const lc = w.toLowerCase();
    if (i > 0 && LC_PARTICLES.has(lc)) return lc;
    return w.split(/([- '])/).map(p => /^[A-Za-zÀ-ÿ]/.test(p) ? (p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()) : p).join("");
  }).join(" ");
}

/** Capitalize first character of a sentence/free-text string, leave the rest intact. */
export function capitalizeFirst(s) {
  if (!s) return "";
  const str = String(s);
  if (!str.length) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Format ISO date "2026-05-29" -> "29/05/2026". Returns "—" if empty/invalid. */
export function fmtDate(iso) {
  if (!iso) return "—";
  // Try YYYY-MM-DD or full ISO with time
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  // Fallback: parse Date
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
}

/** Build a WhatsApp wa.me link from raw phone (CH default). */
export function whatsappLink(phone, message = "") {
  if (!phone) return null;
  // Keep digits only; if starts with 0, replace with 41 (CH); if no leading + or country, prepend 41
  let p = String(phone).replace(/[^\d+]/g, "");
  if (p.startsWith("+")) p = p.slice(1);
  if (p.startsWith("00")) p = p.slice(2);
  if (p.startsWith("0")) p = "41" + p.slice(1);
  if (p.length < 10) return null;
  const base = `https://wa.me/${p}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

// ============ SWISS ACCOUNTING NUMBER FORMATTING ============
// Standard contabile Wertia (Ticino):
//   - migliaia separate da apostrofo: 1'234
//   - decimali separati da virgola:   1'234,56
//   - sempre 2 decimali per importi CHF (es. 1'234,00 e non 1'234)
//
// L'API `Intl.NumberFormat` non ha un locale built-in che produca apostrofo+virgola
// (de-CH e it-CH usano entrambi il punto decimale), quindi formattiamo via
// `de-CH` (che dà apostrofo migliaia + punto decimale) e poi sostituiamo il
// punto decimale con la virgola.

const _nfMoneyRaw = new Intl.NumberFormat("de-CH", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const _nfIntRaw = new Intl.NumberFormat("de-CH", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** Sostituisce SOLO l'ultimo punto della stringa (separatore decimale) con la
 *  virgola. Lascia inalterati gli apostrofi delle migliaia. */
function _periodToComma(s) {
  const i = s.lastIndexOf(".");
  return i < 0 ? s : `${s.slice(0, i)},${s.slice(i + 1)}`;
}

/**
 * Swiss accounting money format: 1234.5 -> "1'234,50".
 * Sempre 2 decimali. Empty/NaN -> "0,00".
 */
export function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0,00";
  return _periodToComma(_nfMoneyRaw.format(n));
}

/**
 * Convenience for CHF amounts: returns "CHF 1'234,50".
 */
export function fmtChf(v) {
  return `CHF ${fmtMoney(v)}`;
}

/**
 * Integer count format: 1234 -> "1'234". Nessun decimale, apostrofo migliaia.
 */
export function fmtInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return _nfIntRaw.format(Math.round(n));
}

/**
 * Percent format: 12.345 -> "12,3 %". Virgola decimale come gli importi CHF.
 * Se `digits` è 0, returna "12 %".
 */
export function fmtPct(v, digits = 1) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0 %";
  const raw = new Intl.NumberFormat("de-CH", {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(n);
  return `${_periodToComma(raw)} %`;
}

/**
 * Parse a Swiss-formatted accounting number back to a JS number.
 * Accepts "1'234.50", "1'234,50", "1,234.50", "1234.5", etc.
 */
export function parseSwissNumber(s) {
  if (s == null || s === "") return 0;
  if (typeof s === "number") return s;
  let str = String(s).trim().replace(/['\u00A0\s]/g, "");
  // If the string contains both "." and "," → assume "." is thousands and "," is decimal (Italian)
  if (str.includes(",") && str.includes(".")) {
    if (str.lastIndexOf(",") > str.lastIndexOf(".")) {
      str = str.replace(/\./g, "").replace(",", ".");
    } else {
      str = str.replace(/,/g, "");
    }
  } else if (str.includes(",")) {
    str = str.replace(",", ".");
  }
  const n = parseFloat(str);
  return Number.isFinite(n) ? n : 0;
}

// (Old _nfPct helper removed — fmtPct now formats inline.)

/** Look up Swiss PLZ via zippopotam (free, no key). Returns {city, canton} or null. */
export async function lookupSwissPlz(plz) {
  if (!plz || !/^\d{4}$/.test(String(plz).trim())) return null;
  try {
    const res = await fetch(`https://api.zippopotam.us/ch/${plz}`);
    if (!res.ok) return null;
    const data = await res.json();
    const place = (data.places || [])[0];
    if (!place) return null;
    return {
      city: place["place name"] || "",
      canton: place["state"] || "",
      canton_abbr: place["state abbreviation"] || "",
    };
  } catch { return null; }
}
